import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorMode,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  Button,
  Flex,
  IconButton,
} from "@chakra-ui/react";
import { SearchIcon, ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import CreateUserForm from "../components/CreateUserForm";
import { useTranslation } from "react-i18next";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const { colorMode } = useColorMode();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/accounts/users`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(t("usersPage.errors.fetchFailed"));
        }

        const data = await response.json();

        // Check if data is an array, if not try to extract users from the response
        let usersData = Array.isArray(data) ? data : [];

        // If data is an object, try to find a users array in it
        if (!Array.isArray(data) && typeof data === "object") {
          usersData = data.users || [];
        }

        // Ensure we have an array before setting state
        if (!Array.isArray(usersData)) {
          throw new Error(t("usersPage.errors.invalidData"));
        }

        setUsers(usersData);
        setFilteredUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [t]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter((user) => {
      return (
        user.username.toLowerCase().includes(query) ||
        user.first_name.toLowerCase().includes(query) ||
        user.last_name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    });
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const handleUserCreated = () => {
    setIsCreateModalOpen(false);
    // Refresh the users list
    const fetchUsers = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/accounts/users`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(t("usersPage.errors.fetchFailed"));
        }

        const data = await response.json();
        let usersData = Array.isArray(data) ? data : [];
        if (!Array.isArray(data) && typeof data === "object") {
          usersData = data.users || [];
        }

        setUsers(usersData);
        setFilteredUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message);
      }
    };

    fetchUsers();
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...filteredUsers];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [filteredUsers, sortConfig]);

  if (loading) {
    return (
      <Box
        minH="calc(100vh - 4rem)"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      minH="calc(100vh - 4rem)"
      py={8}
      bg={colorMode === "dark" ? "gray.800" : "gray.50"}
    >
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" align="center">
            <Heading
              as="h2"
              size="xl"
              color={colorMode === "dark" ? "white" : "gray.800"}
            >
              {t("usersPage.title")}
            </Heading>
            <Button
              colorScheme="blue"
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<span>+</span>}
            >
              {t("usersPage.createUser")}
            </Button>
          </Flex>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon
                color={colorMode === "dark" ? "gray.400" : "gray.500"}
              />
            </InputLeftElement>
            <Input
              placeholder={t("usersPage.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg={colorMode === "dark" ? "gray.700" : "white"}
              borderColor={colorMode === "dark" ? "gray.600" : "gray.200"}
              color={colorMode === "dark" ? "white" : "gray.800"}
              _placeholder={{
                color: colorMode === "dark" ? "gray.400" : "gray.500",
              }}
              _hover={{
                borderColor: colorMode === "dark" ? "blue.300" : "blue.500",
              }}
              _focus={{
                borderColor: colorMode === "dark" ? "blue.300" : "blue.500",
                boxShadow: "0 0 0 1px",
              }}
            />
          </InputGroup>
          {filteredUsers.length === 0 ? (
            <Text color={colorMode === "dark" ? "gray.300" : "gray.600"}>
              {searchQuery
                ? t("usersPage.noUsersFound")
                : t("usersPage.noUsers")}
            </Text>
          ) : (
            <Box
              overflowX="auto"
              borderRadius="lg"
              borderWidth="1px"
              borderColor={colorMode === "dark" ? "gray.600" : "gray.200"}
            >
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>
                      <Flex align="center" gap={2}>
                        {t("usersPage.table.username")}
                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={
                            sortConfig.key === "username" ? (
                              sortConfig.direction === "ascending" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            ) : (
                              <ChevronUpIcon />
                            )
                          }
                          onClick={() => handleSort("username")}
                          aria-label="Sort by username"
                        />
                      </Flex>
                    </Th>
                    <Th>
                      <Flex align="center" gap={2}>
                        {t("usersPage.table.email")}
                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={
                            sortConfig.key === "email" ? (
                              sortConfig.direction === "ascending" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            ) : (
                              <ChevronUpIcon />
                            )
                          }
                          onClick={() => handleSort("email")}
                          aria-label="Sort by email"
                        />
                      </Flex>
                    </Th>
                    <Th>
                      <Flex align="center" gap={2}>
                        {t("usersPage.table.firstName")}
                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={
                            sortConfig.key === "first_name" ? (
                              sortConfig.direction === "ascending" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            ) : (
                              <ChevronUpIcon />
                            )
                          }
                          onClick={() => handleSort("first_name")}
                          aria-label="Sort by first name"
                        />
                      </Flex>
                    </Th>
                    <Th>
                      <Flex align="center" gap={2}>
                        {t("usersPage.table.lastName")}
                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={
                            sortConfig.key === "last_name" ? (
                              sortConfig.direction === "ascending" ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )
                            ) : (
                              <ChevronUpIcon />
                            )
                          }
                          onClick={() => handleSort("last_name")}
                          aria-label="Sort by last name"
                        />
                      </Flex>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedUsers.map((user) => (
                    <Tr key={user.id}>
                      <Td>{user.username}</Td>
                      <Td>{user.email}</Td>
                      <Td>{user.first_name}</Td>
                      <Td>{user.last_name}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </VStack>
      </Container>

      <CreateUserForm
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onUserCreated={handleUserCreated}
      />
    </Box>
  );
};

export default Users;
